import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./PurchaseApprovalForm.module.css";
import { requestAPI } from "../services/api";
import {
  ReadOnlyField,
  Message,
  ReadOnlyTextArea,
} from "./common/CommonComponents";
import AttachmentManager from "./AttachmentManager";

import LoadingSpinner from "./common/LoadingSpinner";
import logo from "../Logo.png";

import MapStatusClass from "../services/StatusClassMapper";

const PurchaseApprovalReqForm = () => {
  const { slug } = useParams();
  const [formData, setFormData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [comment, setComment] = useState("");

  // ✅ Fetch request data from backend
  useEffect(() => {
    if (!slug) {
      setMessage({
        text: "No request specified in URL",
        type: "error",
      });
      return;
    }

    const fetchRequest = async () => {
      setMessage({ text: "", type: "" });
      setLoading(true);
      try {
        const response = await requestAPI.getRequest(slug);
        setFormData(response.data);
        setComment(response.data.comment || "");
        setMessage({
          text: "",
          type: "",
        });
      } catch (err) {
        console.error("Error fetching data:", err);
        setMessage({
          text:
            err.response?.data?.message ||
            "Request not found or error fetching data.",
          type: "error",
        });
        setFormData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchRequest();
  }, [slug]);

  // ✅ Submit approval
  const handleRequestForApproval = async () => {
    if (!formData) return;
    setSubmitting(true);
    try {
      const response = await requestAPI.postSubmitForApproval(
        slug,
        {
          comment: comment,
        },
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setMessage({
        text: response.data.message,
        type: response.data.status ? "success" : "error",
      });

      // Update local data with the new status if needed
      setFormData((prev) => ({
        ...prev,
        RequestStatus: response.data.data.RequestStatus, // or whatever status the API returns
        comment: comment,
      }));
    } catch (err) {
      console.error("Error submitting form:", err);
      setMessage({
        text: err.response?.data?.message || "Error submitting form",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading && !formData) {
    return (
      <div className={styles.loadingContainer}>
        <LoadingSpinner />
        <p>Loading purchase request...</p>
      </div>
    );
  }

  if (!formData) {
    return (
      <div className={styles.errorContainer}>
        <h2>Request Not Found</h2>
        <p>The purchase request could not be loaded.</p>
      </div>
    );
  }
  return (
    <div className={styles.container}>
      <div className={styles.form}>
        {/* Header */}
        <div className={styles.header}>
          <img src={logo} alt="Company Logo" className={styles.logo} />
          <h1 className={styles.title}>Supplier Request Form</h1>
          <div className={styles.statusBadge}>
            <span
              className={`${styles.status} ${MapStatusClass(
                formData.RequestStatus
              )}`}
            >
              {formData.RequestStatus}
            </span>
          </div>
        </div>

        <Message text={message.text} type={message.type} />

        {/* Side by Side Section - Request Details & Attachments */}
        <div className={styles.sideBySideSection}>
          {/* Left Column - Request Details */}
          <div className={styles.leftColumn}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Request Details</h2>
              <div className={styles.detailsGrid}>
                <ReadOnlyField
                  label="Request Number"
                  value={formData.RequestID}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Request Date"
                  value={formData.formatted_request_date}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Supplier"
                  value={formData.Supplier}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Project"
                  value={formData.ProjectName}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Requested By"
                  value={formData.RequestedBy}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Created By"
                  value={formData.PreparedBy}
                  className={styles.detailField}
                />
                <ReadOnlyField
                  label="Reviewed By"
                  value={
                    <div className={styles.reviewedByRow}>
                      <span>{formData.ReviewedBy}</span>
                      <span className={styles.reviewTimestamp}>
                        {formData.formatted_reviewed_date}
                      </span>
                    </div>
                  }
                  className={styles.detailField}
                />
              </div>
            </div>
          </div>

          {/* Right Column - Attachments */}
          <div className={styles.rightColumn}>
            <div className={styles.section}>
              <h2 className={styles.sectionTitle}>Attachments</h2>
              <AttachmentManager requestId={formData.RequestID} />
            </div>
          </div>
        </div>

        {/* Items Table */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Items</h2>
          <div className={styles.tableContainer}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.th}>Item Description</th>
                  <th className={`${styles.th} ${styles.center}`}>Quantity</th>
                  <th className={`${styles.th} ${styles.right}`}>Unit Price</th>
                  <th className={`${styles.th} ${styles.right}`}>Total</th>
                </tr>
              </thead>
              <tbody>
                {formData.request_details?.map((item) => (
                  <tr key={item.RequestItemID} className={styles.tr}>
                    <td className={styles.td}>{item.ItemDesc}</td>
                    <td className={`${styles.td} ${styles.center}`}>
                      {item.Qty}
                    </td>
                    <td className={`${styles.td} ${styles.right}`}>
                      {parseFloat(item.UnitPrice).toLocaleString("en-US", {
                        style: "currency",
                        currency: formData.Currency || "AED",
                      })}
                    </td>
                    <td className={`${styles.td} ${styles.right}`}>
                      {parseFloat(item.total_price).toLocaleString("en-US", {
                        style: "currency",
                        currency: formData.Currency || "AED",
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className={styles.footerRow}>
                  <td className={styles.td} colSpan={3}>
                    <strong>Grand Total</strong>
                  </td>
                  <td
                    className={`${styles.td} ${styles.right} ${styles.grandTotal}`}
                  >
                    <strong>
                      {parseFloat(formData.total_amount).toLocaleString(
                        "en-US",
                        {
                          style: "currency",
                          currency: formData.Currency || "AED",
                        }
                      )}
                    </strong>
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Request Remarks */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Remarks</h2>
          <ReadOnlyTextArea value={formData.RequestRemarks} />
        </div>

        {/* Comment Section */}
        <div className={styles.section}>
          <h2 className={styles.sectionTitle}>Comment</h2>
          <textarea
            className={styles.commentInput}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            placeholder="Add any comments or notes here..."
            disabled={submitting}
            rows={4}
          />
        </div>

        {/* Actions */}
        <div className={styles.actions}>
          <button
            type="button"
            className={styles.submitButton}
            onClick={handleRequestForApproval}
            disabled={submitting || loading}
          >
            {submitting ? (
              <>
                <span className={styles.spinner}></span>
                Processing...
              </>
            ) : (
              "Submit for Approval"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PurchaseApprovalReqForm;
